import {
  customer_api,
  loan_application_api,
  person_api,
} from '@axo/shared/data-access/api';
import {
  customer,
  Debtor,
  loan_application,
} from '@axo/shared/data-access/types';
import { Broker } from '@axo/shared/data-access/types/broker';
import { MarketCountryCode } from '@axo/shared/types';
import { reduce } from 'lodash';
import { convertToFormData } from './convertToFormData';
import { ReloadedState } from './types/dataTypes';

const hasMarketingConsent = (
  MarketingConsent?: customer.Customer['MarketingConsent']
) =>
  !!MarketingConsent &&
  !!MarketingConsent.ConsentText &&
  !MarketingConsent.WithdrawnAt;

const getFrontendIds = (data: { ID: string }[] | undefined, prefix: string) => {
  const result = {};

  if (!data?.length) return result;

  for (const i in data) {
    const item = data[i];
    Object.assign(result, {
      [`${prefix}/${i}`]: item.ID,
    });
  }

  return result;
};

const getLeadsFrontendIds = (
  data: loan_application.Lead[] | undefined,
  prefix: string
) => {
  const result = {};

  if (!data?.length) return result;

  for (const lead of data) {
    Object.assign(result, {
      [`${prefix}/${loan_application.Partner[lead.Partner]}`]: lead.ID,
    });
  }

  return result;
};

const getExpenseFrontendIds = (
  data: loan_application.Expense[] | undefined,
  prefix: string
) => {
  const result = {};

  if (!data?.length) return result;

  for (const expense of data) {
    Object.assign(result, {
      [`${prefix}/${loan_application.ExpenseKind[expense.Kind]}`]: expense.ID,
    });
  }

  return result;
};

const getDebtFrontendIds = (debts: loan_application.Debt[]) => {
  const data = debts.map((debt) => {
    const prefix = debt.Debtor === Debtor.CoApplicant ? 'CoDebt' : 'Debt';
    return {
      [`${prefix}/${debt.Type}`]: debt.ID,
    };
  });
  return reduce(data, (acc, curr) => ({ ...acc, ...curr }));
};

export type Tokens = {
  customerJWT?: string | null;
  applicationId?: string | null;
  magicToken?: string | null;
};

interface Props {
  marketCountry: MarketCountryCode;
  baseUrl: string;
  tokens: Tokens;
}

const loadApplicationByTokenOrId = async (
  baseUrl: string,
  tokens: Tokens
): Promise<{
  JWT: string;
  loanApplication: loan_application.LoanApplication;
}> => {
  const { customerJWT, applicationId, magicToken } = tokens;

  if (magicToken) {
    const { JWT, ...loanApplication } =
      await loan_application_api.getLoanApplicationFromMagicToken(
        baseUrl,
        magicToken
      );
    return { loanApplication, JWT };
  }

  if (!customerJWT || !applicationId) {
    throw new Error('Missing tokens');
  }

  const api = {
    url: { service: baseUrl },
    token: customerJWT,
  };
  const loanApplication = await loan_application_api.getLoanApplication(api)(
    applicationId
  );

  return { loanApplication, JWT: customerJWT };
};

export const reloadState = async ({
  marketCountry,
  baseUrl,
  tokens,
}: Props): Promise<
  ReloadedState & { JWT: string; loanApplicationId: string; broker?: Broker }
> => {
  const { loanApplication, JWT } = await loadApplicationByTokenOrId(
    baseUrl,
    tokens
  );

  const id = loanApplication.ID;
  if (!loanApplication.CustomerID)
    throw new Error('cannot reload state without CustomerID');

  const api = { url: { service: baseUrl }, token: JWT };
  const [customer, debts, cars, children, person, coPerson, expenses] =
    await Promise.all([
      customer_api.get(api)(loanApplication.CustomerID),
      loan_application_api.getAllDebts(api)(id),
      loan_application_api.getAllCars(api)(id),
      loan_application_api.getAllChildren(api)(id),
      loanApplication.PersonID
        ? person_api.getPerson(api)(loanApplication.PersonID)
        : undefined,
      loanApplication.CoApplicantPersonID
        ? person_api.getPerson(api)(loanApplication.CoApplicantPersonID)
        : undefined,
      loan_application_api.getAllExpenses(api)(id),
    ]);

  const data = convertToFormData({
    marketCountry,
    loanApplication,
    customer,
    debts,
    cars,
    children,
    person,
    coPerson,
    expenses,
  });

  return {
    data,
    preconditions: {
      ids: {
        debts: {
          ...getDebtFrontendIds(debts),
        },
        cars: { ...getFrontendIds(cars, 'Car') },
        children: { ...getFrontendIds(children, 'Child') },
        expenses: {
          ...getExpenseFrontendIds(expenses, 'Expense'),
        },
      },
      customerId: loanApplication.CustomerID ?? undefined,
      customerEmail: customer.Email,
      customerMobilePhoneCountryCode: customer.MobilePhoneCountryCode,
      customerMobilePhoneNumber: customer.MobilePhoneNumber,
      customerHasConsentedToMarketing: hasMarketingConsent(
        customer.MarketingConsent
      ),
      personId: loanApplication.PersonID,
      loanApplicationId: loanApplication.ID,
      amountOfChildrenIds: children.map((child) => child.ID),
      coPersonId: loanApplication.CoApplicantPersonID
        ? loanApplication.CoApplicantPersonID
        : undefined,
      coCustomerId: loanApplication.CoApplicantCustomerID
        ? loanApplication.CoApplicantCustomerID
        : undefined,
      customerJWT: JWT,
      children,
      expenses,
    },
    JWT: JWT,
    loanApplicationId: loanApplication.ID,
    broker: loanApplication.Meta?.Broker,
  };
};
